import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import useUserStore from "@store/useUserStore";
import { SnackbarProvider } from "contexts/SnackbarContext";
import { initializeGA4, trackPageView } from "utils/analytics"; // 새로운 GA4 유틸리티 함수 import
import StaffRoute from "pages/StaffRoute"; // 스태프 전용 라우트
import ErrorPage from "pages/error/ErrorPage";
import ErrorBoundary from "components/common/ErrorBoundary";
import BottomNavigation from "components/BottomNavigation"; // 하단 네비게이션 바 컴포넌트
import MainPage from "pages/main/MainPage"; // 메인 페이지
import LoginPage from "pages/login/LoginPage"; // 로그인 페이지
import Term from "pages/login/term/Term"; // 이용약관 페이지
import User from "pages/mypage/User"; // 내정보관리 페이지
import UserEdit from "pages/mypage/UserEdit";
import InstructorProfileEdit from "pages/mypage/InstructorProfileEdit";
import AddressEdit from "pages/mypage/AddressEdit";
import MyReservation from "pages/mypage/MyReservation";

import TravelProducts from "pages/TravelProducts"; // 여행 상품 목록 페이지
import SignupPage from "pages/login/SignupPage"; // 회원가입 페이지
import PaymentResult from "pages/payment/PaymentResult"; // 결제 결과 페이지
import LocationPage from "pages/location/LocationPage";
import LocationDetail from "pages/location/LocationMap";
import TourDetail from "pages/store/tour/TourDetail";
import ClassPageDetail from "pages/store/class/ClassDetail";
import ClassPage from "pages/store/class/Class";
import CreateClass from "pages/schedule/CreateClass";
import VerifyLicense from "pages/mypage/VerifyLicense";
import MyTicket from "pages/mypage/MyTicket";
import PaymentInfo from "pages/payment/PaymentInfo";
import AdminAttendance from "pages/Admin/AdminAttendance";
import FindIdAndPW from "pages/login/FindIdAndPW";
import Settings from "pages/settings/Settings";
import ClassReservationDetail from "pages/reservation/ReservationDetail";
import MyBuccl from "pages/mybuccl/MyBuccl";
import BucketList from "pages/bucketlist/BucketList";
import ShoppingPage from "pages/store/product/Product";
import AdminAttendanceDetail from "pages/Admin/AdminAttendanceDetail";
import ProductDetail from "pages/store/product/ProductDetail";

import MyInterest from "pages/mybuccl/MyInterest";
import LikedPosts from "pages/mybuccl/LikedPosts";
import LessonHistory from "pages/mybuccl/LessonHistory";
import TourHistory from "pages/mybuccl/TourHistory";
import TourLikes from "pages/mybuccl/TourLikes";
import Reservation from "pages/reservation/Reservation";
import Store from "pages/store/Store";
import StoreCategoryWrap from "pages/store/CategoryWrap";
import Cart from "pages/cart/Cart";
import CartCheckout from "pages/cart/CartCheckout";
import Withdraw from "pages/withdraw/Withdraw";
import OrderHistory from "pages/orderHistory/OrderHistory";
import OrderDetail from "pages/orderHistory/OrderDetail";
import CommunityList from "pages/community/CommunityList";
import CommunityCreate from "pages/community/CommunityCreate";
import CommunityDetail from "pages/community/CommunityDetail";
import CommunityEdit from "pages/community/CommunityEdit";
import CommunitySearch from "pages/community/CommunitySearch";
import CommunityUserPosts from "pages/community/CommunityUserPosts";
import BlockedUsers from "pages/community/BlockedUsers";
import MyReports from "pages/community/MyReports";
import PushSetting from "pages/mypage/PushSetting";
import AlertPage from "pages/alert/AlertPage";
import NoticeDetail from "pages/alert/NoticeDetail";
import PullToRefresh from "components/common/PullToRefresh";
import useDeepLink from "hooks/useDeepLink";
import useNativeTokenSync from "hooks/useNativeTokenSync";
import usePublicIdBackfill from "hooks/usePublicIdBackfill";
import "./index.scss";
import QRPromoCard from "components/mainPromotion/QRPromoCard";
import AdminRegistTour from "./pages/Admin/AdminRegistTour";
import AdminMyTourList from "./pages/Admin/AdminMyTourList";
import TourCalendarPage from "pages/store/tour/TourCalendar";
import TourPage from "pages/store/tour/Tour";
import { shouldShowBottomNavigation } from "constants/bottomNavigation";
import ExternalPayment from "pages/payment/ExternalPayment";
import InstructorMonthlyStats from "pages/instructor/InstructorMonthlyStats";
import InstructorProfile from "pages/instructor/InstructorProfile";
import PbHistory from "pages/pb/PbHistory";
import ReviewWrite from "pages/review/ReviewWrite";
import MyReviews from "pages/review/MyReviews";
import { renderMessageFeatureRoutes } from "routes/MessageFeatureRoutes";
import LessonRequestStart from "pages/lessonRequest/LessonRequestStart";
import LessonRequestCompose from "pages/lessonRequest/LessonRequestCompose";
import MyLessonRequests from "pages/lessonRequest/MyLessonRequests";
import LessonRequestDetail from "pages/lessonRequest/LessonRequestDetail";
import InstructorLessonRequests from "pages/lessonRequest/InstructorLessonRequests";
import PromotionRoute from "pages/promotion/PromotionRoute"; // 프로모션/이벤트 페이지 (/promotion/:name)
import RequestPromoCard from "./components/mainPromotion/RequestPromoCard";

import { isDevEnv } from "./utils/index";
// import HomeTest from "pages/main/HomeTest";

function OrderDetailRedirect() {
    const { orderId } = useParams();
    return <Navigate to={`/order-history/${orderId}`} replace />;
}

function InstructorScheduleRedirect() {
    const { id } = useParams();
    return <Navigate to={`/reservation/class/${id}`} replace />;
}

const GA_TRACKING_ID = process.env.REACT_APP_GA_TRACKING_ID;

function RouteTracker({ children }) {
    const location = useLocation();
    const [initialized, setInitialized] = useState(false);

    // GA4 초기화
    useEffect(() => {
        if (!GA_TRACKING_ID) return;
        // ReactGA.initialize(GA_TRACKING_ID); // 기존 초기화 주석 처리
        initializeGA4(GA_TRACKING_ID); // 새로운 GA4 초기화 함수 사용
        setInitialized(true);
    }, []);

    // location 변경 감지시 pageview 이벤트 전송
    useEffect(() => {
        if (initialized) {
            // ReactGA.set({ page: location.pathname }); // 기존 방식 주석 처리
            // ReactGA.send("pageview"); // 기존 방식 주석 처리
            trackPageView(location.pathname); // 새로운 페이지뷰 추적 함수 사용
        }
    }, [initialized, location]);

    return children;
}

function App() {
    const location = useLocation();
    const [path, setPath] = useState(location.pathname);
    useDeepLink();
    useNativeTokenSync();
    usePublicIdBackfill();
    // const userInfo = useUserStore((state) => state.userInfo);


    useEffect(() => {
        setPath(location.pathname);
        const ptrContainer = document.querySelector('.ptr-scroll-container');
        if (ptrContainer) {
            ptrContainer.scrollTop = 0;
        }
    }, [location.pathname]);


    const isAdmin = path.includes("/admin");

    // Pull-to-Refresh(당겨서 새로고침) 제스처만 비활성화할 상세 페이지
    // (장바구니 / 주문 상세 / 상품 상세 / 클래스 상세 / 투어 상세)
    // 스크롤 컨테이너(.ptr-scroll-container)는 유지해야 스크롤이 살아있으므로 disabled가 아닌 suppressRefresh 사용
    const isPtrExcludedPage =
        path === "/cart" ||
        path.startsWith("/order-history/") ||
        path.includes("/product/detail/") ||
        path.includes("/class/detail/") ||
        path.includes("/tour/detail/");


    return (
        <>
        {!isAdmin && <QRPromoCard />}
        {/* {!isAdmin && <RequestPromoCard />} */}
        <div
            id={isAdmin ? "admin-container" : "max-container"}
        >
            <PullToRefresh disabled={isAdmin} suppressRefresh={isPtrExcludedPage}>
            <SnackbarProvider>
                <ErrorBoundary>
                <RouteTracker>
                    <Routes>
                        <Route path="/" element={<MainPage />} />
                        {/* <Route path="/home-test" element={<HomeTest />} /> */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/find-id" element={<FindIdAndPW />} />
                        <Route path="/signup" element={<SignupPage />} />
                        <Route path="/withdraw" element={<Withdraw />} />
                        <Route path="/policy/:type" element={<Term />} />
                        <Route path="/settings" element={<Settings />} />
                        <Route path="/mypage" element={<Navigate to="/settings" replace />} />
                        <Route path="/mypage/user" element={<User />} />
                        <Route
                            path="/mypage/setting"
                            element={<Navigate to="/settings" replace />}
                        />
                        <Route path="/alert" element={<AlertPage />} />
                        <Route path="/notice/:noticeId" element={<NoticeDetail />} />
                        <Route
                            path="/mypage/address/edit"
                            element={<AddressEdit />}
                        />
                        <Route
                            path="/mypage/user/edit"
                            element={<UserEdit />}
                        />
                        <Route
                            path="/mypage/instructor/edit"
                            element={<InstructorProfileEdit />}
                        />
                        <Route
                            path="/verify/:type"
                            element={<VerifyLicense />}
                        />
                        <Route path="/myticket" element={<MyTicket />} />
                        <Route
                            path="/myreservation"
                            element={<MyReservation />}
                        />

                        {/* 강사 전용 라우트 */}
                        <Route element={<StaffRoute />}>
                            <Route
                                path="/attendance"
                                element={<AdminAttendance />}
                            />
                            <Route
                                path="/attendance/:id"
                                element={<AdminAttendanceDetail />}
                            />
                            {/* <Route
                                path="/admin/tour/attendance"
                                element={<AdminTour />}
                            /> */}
                            <Route
                              path="/tour/regist"
                              element={<AdminRegistTour/>}
                            />
                            <Route
                                path="/tour/my"
                                element={<AdminMyTourList />}
                            />
                            <Route
                                path="/tour/edit/:id"
                                element={<AdminRegistTour />}
                            />
                            <Route
                                path="/instructor/stats"
                                element={<InstructorMonthlyStats />}
                            />
                            <Route
                                path="/instructor/schedules/:id"
                                element={<InstructorScheduleRedirect />}
                            />
                        </Route>
                        <Route
                            path="/sports-location"
                            element={<LocationPage />}
                        />
                        <Route
                            path="/sports-location/detail/:id"
                            element={<LocationDetail />}
                        />

                        <Route
                            path="/reservation/class/:classId"
                            element={<ClassReservationDetail />}
                        />
                        <Route
                            path="/reservation/practice/:practiceId"
                            element={<ClassReservationDetail />}
                        />
                        <Route path="/tour/list" element={<TourPage />} />
                        <Route
                            path="/tour/detail/:tourId"
                            element={<TourDetail />}
                        />
                        <Route path="/class" element={<ClassPage />} />
                        <Route
                            path="/class/detail/:id"
                            element={<ClassPageDetail />}
                        />
                        <Route path="/class/create" element={<CreateClass />} />
                        <Route
                            path="/:type/edit/:id" // type = practice or class
                            element={<CreateClass />}
                        />
                        <Route
                            path="/external/payment"
                            element={<ExternalPayment/>}/>
                        <Route
                            path="/payment/:type/:id"
                            element={<PaymentInfo />}
                        />
                        {/* 스포츠 데이터 입력 페이지 */}
                        <Route
                            path="/travel_product"
                            element={<TravelProducts />}
                        />
                        <Route
                            path="/payment-result"
                            element={<PaymentResult />}
                        />
                        <Route
                            path="/app/payment-result"
                            element={<PaymentResult />}
                        />
                        <Route path="/mybuccl" element={<MyBuccl />} />
                        <Route path="/shopping" element={<ShoppingPage />} />
                        <Route
                            path="/product/detail/:productId"
                            element={<ProductDetail />}
                        />

                        <Route
                            path="/mybuccl/interest"
                            element={<MyInterest />}
                        />
                        <Route
                            path="/mybuccl/liked"
                            element={<LikedPosts />}
                        />
                        <Route
                            path="/mybuccl/lessons"
                            element={<LessonHistory />}
                        />
                        <Route
                            path="/mybuccl/tours"
                            element={<TourHistory />}
                        />
                        <Route
                            path="/mybuccl/tour-likes"
                            element={<TourLikes />}
                        />
                        <Route
                            path="/mybuccl/bucketlist"
                            element={<BucketList />}
                        />

                        <Route path="/reservation" element={<Reservation />} />
                        <Route path="/lesson-requests" element={<MyLessonRequests />} />
                        <Route path="/lesson-requests/new" element={<LessonRequestCompose />} />
                        <Route path="/lesson-requests/new/:type" element={<LessonRequestCompose />} />
                        <Route path="/lesson-requests/instructor" element={<InstructorLessonRequests />} />
                        <Route path="/lesson-requests/:id" element={<LessonRequestDetail />} />
                        {renderMessageFeatureRoutes()}
                        <Route path="/store" element={<Store />} />
                        <Route
                            path="/tour"
                            element={<TourCalendarPage />}
                        />
                        <Route
                            path="/store/:category"
                            element={<StoreCategoryWrap />}
                        />
                        <Route path="/cart" element={<Cart />} />
                        <Route
                            path="/cart/checkout"
                            element={<CartCheckout />}
                        />
                        <Route
                            path="/order-history"
                            element={<OrderHistory />}
                        />
                        <Route
                            path="/order-history/:orderId"
                            element={<OrderDetail />}
                        />
                        <Route path="/review/write/:orderId/:itemId" element={<ReviewWrite />} />
                        <Route path="/review/edit/:reviewId" element={<ReviewWrite />} />
                        <Route path="/mypage/reviews" element={<MyReviews />} />
                        <Route
                            path="/orders/:orderId"
                            element={<OrderDetailRedirect />}
                        />

                        {/* 커뮤니티 라우트 */}
                        <Route path="/community" element={<CommunityList />} />
                        <Route path="/community/blocked" element={<BlockedUsers />} />
                        <Route path="/community/reports" element={<MyReports />} />
                        <Route path="/community/search" element={<CommunitySearch />} />
                        <Route path="/community/user/:userId" element={<CommunityUserPosts />} />
                        <Route path="/instructor/:userId" element={<InstructorProfile />} />
                        <Route path="/pb/history/:userId" element={<PbHistory />} />
                        <Route
                            path="/community/create"
                            element={<CommunityCreate />}
                        />
                        <Route
                            path="/community/edit/:postId"
                            element={<CommunityEdit />}
                        />
                        <Route
                            path="/community/:postId"
                            element={<CommunityDetail />}
                        />
                        {/* 프로모션/이벤트 페이지. 실제 화면은 pages/promotion/promotionRegistry.js 에 등록한다. */}
                        <Route
                            path="/promotion/:name"
                            element={<PromotionRoute />}
                        />
                        <Route
                            path="/promotion/:name/:sub"
                            element={<PromotionRoute />}
                        />
                        <Route path="*" element={<ErrorPage status={404} />} />
                    </Routes>
                    {shouldShowBottomNavigation(path) ? (
                        <BottomNavigation />
                    ) : null}
                </RouteTracker>
                </ErrorBoundary>
            </SnackbarProvider>
            </PullToRefresh>
        </div>
        </>
    );
}

export default App;
